import {
  PERIOD_EXCEPTION_LINES,
  PERIOD_EXCEPTIONS,
  type PeriodException,
  type PeriodExceptionLine,
} from '@/shared/api/billing';

/**
 * `validateSearch` of `/billing/exception-hours` (`AM-552`, decision 123 B of the texts the owner approved on
 * 2026-10-25): the row of hours of the exceptions it lists and, when given, the period; without one, the
 * rows of every period not archived, as the card "Excepciones de los períodos" of Inicio adds them up.
 */
export interface ExceptionHoursSearch {
  exception: PeriodException;
  period?: string;
}

export function exceptionHoursSearchSchema(search: Record<string, unknown>): ExceptionHoursSearch {
  const exception = search['exception'];
  const period = search['period'];
  return {
    exception:
      typeof exception === 'string' && (PERIOD_EXCEPTIONS as readonly string[]).includes(exception)
        ? (exception as PeriodException)
        : 'unapproved',
    ...(typeof period === 'string' && period.length > 0 ? { period } : {}),
  };
}

/** The name of each row, the key of `billing:periods.exception.*` the card of the period shows it with. */
export const EXCEPTION_LABEL_KEYS: Record<PeriodException, string> = {
  unapproved: 'billing:periods.exception.unapprovedTimeEntries',
  rejected: 'billing:periods.exception.rejectedTimeEntries',
  unresolved_rate: 'billing:periods.exception.unresolvedRates',
  without_contract: 'billing:periods.exception.workWithoutContract',
  unresolved_cost: 'billing:periods.exception.unresolvedCosts',
};

/**
 * `validateSearch` of `/billing/exception-lines` (`AM-723`, decision 123 B): the row of items or of parts and trips of
 * the exceptions it lists and, when given, the period; without one, the rows of every period not archived.
 */
export interface ExceptionLinesSearch {
  row: PeriodExceptionLine;
  period?: string;
}

export function exceptionLinesSearchSchema(search: Record<string, unknown>): ExceptionLinesSearch {
  const row = search['row'];
  const period = search['period'];
  return {
    row:
      typeof row === 'string' && (PERIOD_EXCEPTION_LINES as readonly string[]).includes(row)
        ? (row as PeriodExceptionLine)
        : 'pending_fixed_price_items',
    ...(typeof period === 'string' && period.length > 0 ? { period } : {}),
  };
}

/** The name of each row, the key of `billing:periods.exception.*` the card of the period shows it with. */
export const EXCEPTION_LINE_LABEL_KEYS: Record<PeriodExceptionLine, string> = {
  pending_fixed_price_items: 'billing:periods.exception.pendingFixedPriceItems',
  pending_ticket_charges: 'billing:periods.exception.pendingTicketCharges',
};
